import { CloseButton, Popover, PopoverButton, PopoverPanel, PopoverBackdrop } from '@headlessui/react'
import cn from 'clsx'
import { Link, useSearchParams } from 'react-router'

import Logo from '@/components/icons/Logo'
import TryDemoButton from '@/components/TryDemoButton'
import { useCurrent } from '@/current'

type Props = {
  isFullWidth?: boolean
  hideBanner?: boolean
}

export default function SiteHeader({ isFullWidth, hideBanner }: Props) {
  const { userSignedIn } = useCurrent()
  const [searchParams] = useSearchParams()
  const demoError = searchParams.get('demo_error') === '1'

  return (
    <>
      {demoError && (
        <div
          role='alert'
          className='bg-red-50 px-3 py-1 text-center text-sm font-semibold text-red-600 in-[.dark]:bg-red-950 in-[.dark]:text-red-400 dark:bg-red-950 dark:text-red-400'
        >
          Couldn&apos;t start the demo. Please try again.
        </div>
      )}
      {!hideBanner && (
        <div className='bg-neki px-3 py-1 text-center font-medium text-gray-900 in-[.dark]:font-semibold dark:font-semibold'>
          <span>Neki, sharded Postgres, is now available.</span>{' '}
          <span className='whitespace-nowrap'>
            <Link
              prefetch='intent'
              to='/blog/introducing-neki'
              className='bg-gray-900 px-sm font-semibold whitespace-nowrap text-white'
            >
              Learn more
            </Link>
          </span>
        </div>
      )}
      <header className='relative mt-4 mb-6 bg-primary'>
        <div
          className={cn('flex flex-col gap-y-3 px-3 sm:px-5', {
            'mx-auto max-w-(--breakpoint-2xl)': isFullWidth,
            'container max-w-7xl': !isFullWidth
          })}
        >
          <div className='grid w-full grid-cols-[auto_1fr] grid-rows-1 items-center lg:items-start lg:gap-3'>
            <Link
              aria-label='Go to homepage'
              className='col-start-1 col-end-2 h-4 w-4 rounded-full text-primary lg:hidden'
              prefetch='intent'
              to='/'
            >
              <Logo height={32} width={32} />
            </Link>

            <div className='group col-start-2 col-end-3 row-start-1 flex shrink-0 items-center justify-end gap-1.5 lg:gap-3'>
              {userSignedIn ? (
                <div className='flex flex-row gap-2 lg:flex-col lg:gap-1 xl:flex-row'>
                  <div className='flex items-center justify-end gap-1 lg:h-4'>
                    <a href='https://app.planetscale.com' className='font-semibold text-primary hover:text-orange'>
                      Dashboard
                    </a>
                  </div>
                  <div className='flex items-center justify-end gap-0.5 lg:h-4'>
                    <TryDemoButton className='hidden btn-sm sm:inline-flex' />
                    <Link prefetch='intent' to='/contact?demo' className='btn btn-sm'>
                      Get in touch
                    </Link>
                  </div>
                </div>
              ) : (
                <div className='flex flex-row gap-2 lg:flex-col lg:gap-1 xl:flex-row'>
                  <div className='flex items-center justify-end gap-1 lg:h-4'>
                    <a
                      href='https://auth.planetscale.com/sign-in'
                      className='font-semibold text-primary hover:text-orange'
                    >
                      Sign in
                    </a>
                  </div>
                  <div className='flex items-center justify-end gap-0.5 lg:h-4'>
                    <TryDemoButton className='hidden btn-sm sm:inline-flex' />
                    <Link prefetch='intent' to='/contact' className='btn btn-sm'>
                      Get in touch
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div className='col-start-1 col-end-2 flex items-center gap-x-3 lg:row-start-1 lg:h-4'>
              <Link
                aria-label='Go to homepage'
                className='col-start-1 col-end-2 hidden h-4 w-4 rounded-full text-primary lg:block'
                prefetch='intent'
                to='/'
              >
                <Logo height={32} width={32} />
              </Link>
              <nav aria-label='Main' data-orientation='horizontal' className='hidden items-center lg:flex'>
                <SiteHeaderNavigation />
              </nav>
            </div>
          </div>

          <details className='lg:hidden'>
            <summary>Navigation</summary>

            <nav className='mt-1 dashed-box p-3'>
              <SiteHeaderNavigation />
            </nav>
          </details>
        </div>
      </header>
    </>
  )
}

function SiteHeaderNavigation() {
  return (
    <ul className='flex flex-wrap gap-x-1 md:flex-nowrap'>
      <li>
        <Popover>
          <PopoverButton className='font-semibold whitespace-nowrap text-primary hover:text-contrast focus-visible:ring-0 ui-open:text-orange'>
            Platform
            <span className='ml-sm inline-block ui-open:rotate-180'>▾</span>
          </PopoverButton>
          <PopoverBackdrop className='fixed inset-0' />
          <PopoverPanel
            anchor='bottom start'
            className='w-40 border border-orange-500 bg-white in-[.dark]:bg-black dark:bg-black'
          >
            <div className='py-1'>
              <CloseButton
                as={Link}
                to='/vitess'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Vitess
              </CloseButton>
              <CloseButton
                as={Link}
                to='/neki'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Neki
              </CloseButton>
              <CloseButton
                as={Link}
                to='/postgres'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Postgres
              </CloseButton>
              <CloseButton
                as={Link}
                to='/metal'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Metal
              </CloseButton>
              <hr className='mt-1 mb-2 border-orange-500' />
              <h4 className='mb-1 px-2 text-orange'>Use cases</h4>
              <CloseButton
                as={Link}
                to='/sharding'
                prefetch='intent'
                className='block px-2 py-1 font-medium text-primary hover:bg-focus hover:text-contrast'
              >
                Sharding
              </CloseButton>
              <CloseButton
                as={Link}
                to='/enterprise'
                prefetch='intent'
                className='block px-2 py-1 font-medium text-primary hover:bg-focus hover:text-contrast'
              >
                Enterprise
              </CloseButton>
              <CloseButton
                as={Link}
                to='/startups'
                prefetch='intent'
                className='block px-2 py-1 font-medium text-primary hover:bg-focus hover:text-contrast'
              >
                Startups
              </CloseButton>
              <hr className='mt-1 mb-2 border-orange-500' />
              <h4 className='mb-1 px-2 text-orange'>Features</h4>
              <CloseButton
                as={Link}
                to='/database-traffic-control'
                prefetch='intent'
                className='block px-2 py-1 font-medium text-primary hover:bg-focus hover:text-contrast'
              >
                Database Traffic Control
              </CloseButton>
            </div>
          </PopoverPanel>
        </Popover>
      </li>
      <li className='text-decoration' role='presentation'>
        |
      </li>
      <li>
        <Popover>
          <PopoverButton className='font-semibold whitespace-nowrap text-primary hover:text-contrast focus-visible:ring-0 ui-open:text-orange'>
            Resources
            <span className='ml-sm inline-block ui-open:rotate-180'>▾</span>
          </PopoverButton>
          <PopoverBackdrop className='fixed inset-0' />
          <PopoverPanel
            anchor='bottom start'
            className='w-40 border border-orange-500 bg-white in-[.dark]:bg-black dark:bg-black'
          >
            <div className='py-1'>
              <CloseButton
                as={Link}
                to='/case-studies'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Case studies
              </CloseButton>
              <CloseButton
                as={Link}
                to='/benchmarks'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Benchmarks
              </CloseButton>
              <CloseButton
                as={Link}
                to='/changelog'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Changelog
              </CloseButton>
              <CloseButton
                as={Link}
                to='/migrate'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Migrate
              </CloseButton>
              <CloseButton
                as={Link}
                to='/cloudflare'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Using with Cloudflare
              </CloseButton>
              <a
                href='https://support.planetscale.com/hc/en-us'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
                rel='nofollow noopener noreferrer'
                target='_blank'
              >
                Support
                <span className='ml-1 inline-block -translate-y-px font-medium text-orange'>↗</span>
              </a>
              <CloseButton
                as={Link}
                to='/events'
                prefetch='intent'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
              >
                Events
              </CloseButton>
              <a
                href='https://trust.planetscale.com'
                className='block px-2 py-1 font-semibold text-primary hover:bg-focus hover:text-contrast'
                rel='nofollow noopener noreferrer'
                target='_blank'
              >
                Trust Center
                <span className='ml-1 inline-block -translate-y-px font-medium text-orange'>↗</span>
              </a>
            </div>
          </PopoverPanel>
        </Popover>
      </li>
      <li className='text-decoration' role='presentation'>
        |
      </li>
      <li>
        <Link className='font-semibold text-primary hover:text-contrast' prefetch='intent' to='/blog'>
          Blog
        </Link>
      </li>
      <li className='text-decoration' role='presentation'>
        |
      </li>
      <li>
        <a className='font-semibold text-primary hover:text-contrast' href='/docs'>
          Documentation
        </a>
      </li>
      <li className='text-decoration' role='presentation'>
        |
      </li>
      <li>
        <Link className='font-semibold text-primary hover:text-contrast' prefetch='intent' to='/pricing'>
          Pricing
        </Link>
      </li>
      <li className='text-decoration' role='presentation'>
        |
      </li>
      <li>
        <Link className='font-semibold text-primary hover:text-contrast' prefetch='intent' to='/migrate'>
          Migrate
        </Link>
      </li>
    </ul>
  )
}
